import { useState, useEffect } from "react";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DocumentUpload from "../../components/DocumentUpload";
import InputField from "../../components/InputField";
import DropDown from "../../components/DropDown";
import { getStateFromGST } from "../../constants/gstStateCodes";
import { toast } from "react-toastify";
import {
  validatePAN,
  validateGST,
  validateMobile,
  validateIFSC,
  validateAccountNumber,
  validateAadhar,
} from "../../utils/validationUtils";
import { validateGSTPANMatch } from "../../utils/gstPanValidation";
import { uploadOnboardingDocumentAction } from "../../actions/uploadOnboardingDocumentAction";
import { convertToBoolean } from "../../utils/dataConversionUtils";
import { getUserData } from "../../utils/authnicate";
import { fetchKYCDetailsByIdAction } from "../../actions/fetchKYCDetailsByIdAction";
import { useAadharVisibilityData } from "./AadharVisibilityData";
import {
  ENTITY_INFO_FIELDS,
  GST_FIELDS,
  MSME_FIELDS,
  CONTACT_FIELDS,
  BANK_FIELDS,
  ALL_FIELDS,
  UTILITY_FIELDS,
  THIRD_PARTY_FIELDS,
  ENTITY_TYPE_DOCUMENTS,
  DOCS_BANK,
  DOC_KEY_TO_API_TYPE,
} from "./UploadDetailsFields";
import { sendEntityKYCFormDataAction } from "../../actions/entityKYCFormDataAction";
import { parseDeficiencies } from "../../utils/deficiencyUtils";
import usePincodeAutofill from "../../hooks/usePincodeAutofill";

export {
  ENTITY_INFO_FIELDS,
  GST_FIELDS,
  MSME_FIELDS,
  CONTACT_FIELDS,
  BANK_FIELDS,
  UTILITY_FIELDS,
  THIRD_PARTY_FIELDS,
  ENTITY_TYPE_DOCUMENTS,
  DOCS_BANK,
};

// ── Entity-type required documents section ────────────────────────────────────

export const EntityDocumentsSection = ({
  entityType,
  entityFiles,
  onEntityFileChange,
  entityDocInputs = {},
  onEntityDocInputChange,
  onEntityDocInputBlur,
  existingDocuments = [],
  errors = {},
  deficiencyMap = {},
  isDeficiencyMode = false,
  // Aadhar visibility props
  handleEyeIconClick,
  isAadharVisible,
  getAadharDisplayValue,
  isAadharField,
}) => {
  const docs = ENTITY_TYPE_DOCUMENTS[entityType];
  if (!docs || docs.length === 0) return null;

  const getExisting = (key) => {
    const doc = existingDocuments.find((d) => d.key === key);
    return doc?.files?.find((f) => f.isUploaded) || null;
  };

  return (
    <div className="entity-docs-section">
      <p className="entity-docs-note">
        Required documents for <strong>{entityType.replace(/_/g, " ")}</strong>
      </p>
      <div className="entity-docs-list">
        {docs.map((doc) => {
          const isDeficient = !!deficiencyMap[doc.key];
          const uploadable = !isDeficiencyMode || isDeficient;
          const fieldName = doc.inputField?.name;
          const isAadhar =
            fieldName && isAadharField && isAadharField(fieldName);

          return (
            <div
              key={doc.key}
              className={`entity-doc-row ${isDeficient ? "entity-doc-row--deficient" : ""}`}
            >
              {doc.inputField && (
                <InputField
                  name={fieldName}
                  label={`${doc.label} Number`}
                  type="text"
                  value={
                    isAadhar && getAadharDisplayValue
                      ? getAadharDisplayValue(
                          fieldName,
                          entityDocInputs[fieldName] || "",
                        )
                      : entityDocInputs[fieldName] || ""
                  }
                  onChange={
                    uploadable && onEntityDocInputChange
                      ? (e) => onEntityDocInputChange(fieldName, e.target.value)
                      : undefined
                  }
                  onBlur={
                    uploadable && onEntityDocInputBlur
                      ? (e) => onEntityDocInputBlur(fieldName, e.target.value)
                      : undefined
                  }
                  placeholder={doc.inputField.placeholder}
                  maxLength={doc.inputField.maxLength}
                  required={doc.required}
                  error={errors[fieldName] || ""}
                  readOnly={!uploadable}
                  className={isDeficient ? "input--deficient" : ""}
                  showEyeIcon={isAadhar}
                  onEyeIconClick={
                    isAadhar && handleEyeIconClick
                      ? () => handleEyeIconClick(fieldName)
                      : undefined
                  }
                  isAadharVisible={
                    isAadhar && isAadharVisible
                      ? isAadharVisible(fieldName)
                      : false
                  }
                />
              )}
              <div className={isDeficient ? "doc-deficient-wrap" : ""}>
                <DocumentUpload
                  label={`Upload ${doc.label}`}
                  name={doc.key}
                  file={entityFiles[doc.key] || null}
                  existingFile={getExisting(doc.key)}
                  onChange={(e) => {
                    if (!uploadable) return;
                    onEntityFileChange(doc.key, e.target.files[0]);
                  }}
                  required={doc.required}
                  disabled={!uploadable}
                />
                {isDeficient && (
                  <span className="doc-deficient-label">
                    ⚠ Deficiency — please re-upload
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Field renderer ─────────────────────────────────────────────────────────────

export const renderFields = (
  fields,
  formData,
  errors,
  handleChange,
  handleBlur,
) => {
  const rendered = [];
  let i = 0;
  while (i < fields.length) {
    const field = fields[i];
    const show =
      !field.conditionalOn ||
      formData[field.conditionalOn] === field.conditionalValue;

    // Collect all consecutive fields with the same group tag
    if (field.group) {
      const groupName = field.group;
      const groupFields = [];
      while (i < fields.length && fields[i].group === groupName) {
        groupFields.push(fields[i]);
        i++;
      }
      rendered.push(
        <div
          key={groupName}
          className={`field-group field-group--${groupName}`}
        >
          {groupFields.map((gf) => {
            const gShow =
              !gf.conditionalOn ||
              formData[gf.conditionalOn] === gf.conditionalValue;
            if (!gShow) return null;
            const gValue = formData[gf.name] || "";
            const gError = errors[gf.name] || "";
            return (
              <InputField
                key={gf.name}
                name={gf.name}
                label={gf.label}
                type={gf.type}
                value={gValue}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={gf.placeholder}
                error={gError}
                required={gf.required}
                maxLength={gf.maxLength}
                readOnly={gf.readOnly}
              />
            );
          })}
        </div>,
      );
      continue;
    }

    if (!show) {
      i++;
      continue;
    }
    const value = formData[field.name] || "";
    const error = errors[field.name] || "";
    if (field.type === "select") {
      // Resolve options — use entity-type-specific options if available
      const resolvedOptions =
        field.optionsByEntityType?.[formData.entityType] ?? field.options;
      rendered.push(
        <DropDown
          key={field.name}
          name={field.name}
          value={value}
          onChange={handleChange}
          label={field.label}
          options={resolvedOptions}
          error={error}
          required={field.required}
          fullWidth
        />,
      );
    } else {
      rendered.push(
        <InputField
          key={field.name}
          name={field.name}
          label={field.label}
          type={field.type}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={field.placeholder}
          error={error}
          required={field.required}
          maxLength={field.maxLength}
          rows={field.rows}
          readOnly={field.readOnly}
        />,
      );
    }
    i++;
  }
  return rendered;
};

// ── Initial state ──────────────────────────────────────────────────────────────

export const initialDetailsState = {
  parentName: "",
  tradeName: "",
  address: "",
  pinCode: "",
  city: "",
  State: "",
  natureOfBusiness: "",
  isInvoiceRequired: "",
  entityType: "",
  CompanypanNumber: "",
  isGSTRegistered: "",
  utilityType: "",
  utilityProviderName: "",
  utilityAccountNumber: "",
  utilityAddress: "",
  isMSMERegistered: "",
  msmeNumber: "",
  contactPersonName: "",
  contactPersonDesignation: "",
  contactPersonMobile: "",
  contactEmail: "",
  hasBankDetails: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  accountType: "",
  branchName: "",
  beneficiaryName: "",
  thirdPartyRelation: "",
  thirdPartyAccountNumber: "",
  thirdPartyIfscCode: "",
};

// ── Hook ───────────────────────────────────────────────────────────────────────

export const useUploadDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialDetailsState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // GST entries state
  const [gstEntries, setGstEntries] = useState([
    { gstNumber: "", gstState: "", gstAddress: "" },
  ]);
  const [gstFiles, setGstFiles] = useState([null]);
  const [gstErrors, setGstErrors] = useState([""]);

  // Document file states
  const [utilityFile, setUtilityFile] = useState(null);
  const [msmeFile, setMsmeFile] = useState(null);
  const [thirdPartyFile, setThirdPartyFile] = useState(null);
  const [entityFiles, setEntityFiles] = useState({});
  const [entityDocInputs, setEntityDocInputs] = useState({});
  const [bankFiles, setBankFiles] = useState({});
  const [existingDocuments, setExistingDocuments] = useState([]);
  // Existing paths from API prefill — used as fallback when user doesn't re-upload
  const [existingPaths, setExistingPaths] = useState({});
  // isXxx flags from API — passed through as-is in the payload (not derived from hasPath)
  const [existingIsFlags, setExistingIsFlags] = useState({});
  // Deficiency mode — set when any *Def flag is true in the API record
  const [deficiencyMap, setDeficiencyMap] = useState({});
  const [isDeficiencyMode, setIsDeficiencyMode] = useState(false);

  // Stores upload API responses keyed by documentType
  // e.g. { PassbookOrCancelledChequePath: { id, fileName, filePath, documentType, extension } }
  const [uploadedDocs, setUploadedDocs] = useState({});

  const handleEntityFileChange = (key, file) =>
    setEntityFiles((prev) => ({ ...prev, [key]: file }));

  const handleEntityDocInputChange = (name, value) => {
    // Aadhar fields: digits only, max 12
    const aadharFields = [
      "doc_aadhar_number",
      "doc_aadharBoth_numbers",
      "doc_aadharTrustee_number",
      "doc_directorAadhar_number",
    ];
    if (aadharFields.includes(name)) {
      value = value.replace(/\D/g, "").slice(0, 12);
    }
    setEntityDocInputs((prev) => ({ ...prev, [name]: value }));
  };

  // Blur validation for document number inputs
  const handleEntityDocInputBlur = (name, value) => {
    if (!value || !value.trim()) return;
    const validators = {
      doc_aadhar_number: validateAadhar,
      doc_aadharBoth_numbers: validateAadhar,
      doc_aadharTrustee_number: validateAadhar,
      doc_directorAadhar_number: validateAadhar,
      doc_pan_number: validatePAN,
      doc_directorPan_number: validatePAN,
      doc_panTrust_number: validatePAN,
      doc_panTrustee_number: validatePAN,
    };
    const validator = validators[name];
    if (!validator) return;
    const result = validator(value);

    setErrors((prev) => {
      const e = { ...prev };
      if (result !== true) {
        e[name] = result;
        return e;
      }
      delete e[name];

      // Cross-check: PAN and Director PAN must not be the same
      const panValue =
        name === "doc_pan_number"
          ? value.trim().toUpperCase()
          : (entityDocInputs["doc_pan_number"] || "").trim().toUpperCase();
      const directorPanValue =
        name === "doc_directorPan_number"
          ? value.trim().toUpperCase()
          : (entityDocInputs["doc_directorPan_number"] || "")
              .trim()
              .toUpperCase();

      if (
        (name === "doc_pan_number" || name === "doc_directorPan_number") &&
        panValue &&
        directorPanValue &&
        panValue === directorPanValue
      ) {
        e["doc_pan_number"] = "PAN Card and Director PAN must not be the same";
        e["doc_directorPan_number"] =
          "Director PAN must be different from PAN Card";
      } else {
        // Clear the cross-check errors if they no longer apply
        if (
          e["doc_pan_number"] ===
          "PAN Card and Director PAN must not be the same"
        ) {
          delete e["doc_pan_number"];
        }
        if (
          e["doc_directorPan_number"] ===
          "Director PAN must be different from PAN Card"
        ) {
          delete e["doc_directorPan_number"];
        }
      }

      return e;
    });
  };

  const handleBankFileChange = (key, file) =>
    setBankFiles((prev) => ({ ...prev, [key]: file }));

  // const departmentOptions = useDepartmentOptions(); // TODO: use when department dropdown is added
  const [beneficiaryMatchStatus, setBeneficiaryMatchStatus] = useState(null);
  // null = not checked yet | "match" | "mismatch"

  const showThirdParty =
    formData.hasBankDetails === "yes" &&
    formData.tradeName.trim() !== "" &&
    formData.beneficiaryName.trim() !== "" &&
    formData.tradeName.trim().toLowerCase() !==
      formData.beneficiaryName.trim().toLowerCase();

  // URL params from step 1
  const entityCategory = searchParams.get("entityCategory") || "";
  // const entityLOBType = searchParams.get("entityLOBType") || "";         // TODO: show in summary
  // const lobNationality = searchParams.get("lobNationality") || "Domestic"; // TODO: include in payload when needed
  // const aaDepartmentFunction = searchParams.get("aaDepartmentFunction") || ""; // TODO: include in payload when needed
  const contactPersonEmail = searchParams.get("contactPersonEmail") || "";
  const spocEmailAddress = searchParams.get("spocEmailAddress") || "";
  const entityNameFromUrl = searchParams.get("EntityName") || "";
  const onboardingIdFromUrl =
    searchParams.get("onboardingId") ||
    searchParams.get("entityPrimaryId") ||
    "";

  const entityId =
    searchParams.get("entityId") || searchParams.get("EntityId") || "";

  // ── Fetch existing KYC data by onboardingId from URL ──────────────────────
  useEffect(() => {
    if (!onboardingIdFromUrl) return;
    const id = parseInt(onboardingIdFromUrl, 10);
    if (!id) return;

    fetchKYCDetailsByIdAction(id).then(
      ({ success, data: record, documents }) => {
        if (!success || !record) return;

        // ── Nested sub-objects ──────────────────────────────────────────────
        const prop = record.proprietorshipDetails || {};
        const partner = record.partnershipDetails || {};
        const llp = record.llpDetails || {};
        const pvt = record.privatePublicLimitedCompanyDetails || {};
        const trust = record.trustDetails || {};
        const msme = record.msmeDetails || {};
        const utility = record.utilityBillDetails || {};
        const bank = record.bankDetails || {};
        const contact = record.contactPersonDetails || {};
        const pan = record.panDetails || {};
        const aadhar = record.adharDetails || {};

        setExistingDocuments(documents || []);

        // Parse deficiency flags from the raw record
        const { deficiencyMap: dMap, isDeficiencyMode: dMode } =
          parseDeficiencies(record);
        setDeficiencyMap(dMap);
        setIsDeficiencyMode(dMode);

        // Seed existing paths for fallback when user doesn't re-upload
        setExistingPaths({
          AadharCardPath: prop.aadharCardPath || aadhar.aadharCardPath || "",
          PANCardPath: prop.panCardPath || pan.panCardPath || "",
          PartnershipDeedPath:
            partner.partnershipDeedPath || llp.partnershipDeedPath || "",
          LLPCertificatePath: llp.uploadLLPCertificatePath || "",
          AadharOfBothPartnersPath:
            partner.aadharOfBothPartnersPath ||
            llp.aadharOfBothPartnersPath ||
            "",
          CertificateOfIncorporationPath:
            pvt.certificateOfIncorporationPath || "",
          DirectorPANPath: pvt.directorPANPath || "",
          DirectorAadharPath: pvt.directorAadharPath || "",
          MSMECertificatePath: msme.msmeCertificatePath || "",
          UtilityBillDocumentPath: utility.utilityBillDocumentPath || "",
          PassbookOrCancelledChequePath:
            bank.passbookOrCancelledChequePath || "",
          UploadSignedFormPath: bank.uploadSignedFormPath || "",
          TrustDeedPath: trust.trustDeedPath || "",
          PANOfTrustPath: trust.trustPanPath || "",
          AadharOfTrusteePath: trust.trusteeAadharPath || "",
          PANOfTrusteePath: trust.trusteePanPath || "",
        });

        // Seed isXxx flags exactly as received from the API — passed through unchanged in payload
        setExistingIsFlags({
          isPANCard: prop.isPANCard ?? pan.isPANCard ?? null,
          isAadharCard: prop.isAadharCard ?? aadhar.isAadharCard ?? null,
          isPartnershipDeed:
            partner.isPartnershipDeed ?? llp.isPartnershipDeed ?? null,
          isAadharOfBothPartners:
            partner.isAadharOfBothPartners ??
            llp.isAadharOfBothPartners ??
            null,
          isLLPCertificate: llp.isLLPCertificate ?? null,
          isCertificateOfIncorporation:
            pvt.isCertificateOfIncorporation ?? null,
          isDirectorPan: pvt.isDirectorPan ?? null,
          isDirectorAadhar: pvt.isDirectorAadhar ?? null,
          isTrustDeed: trust.isTrustDeed ?? null,
          isPANOfTrust: trust.isPANOfTrust ?? null,
          isPANOfTrustee: trust.isPANOfTrustee ?? null,
          isAadharOfTrustee: trust.isAadharOfTrustee ?? null,
          isMSMECertificate: msme.isMSMECertificate ?? null,
          isUtilityBillDocument: utility.isUtilityBillDocument ?? null,
          isPassbookOrCancelledCheque: bank.isPassbookOrCancelledCheque ?? null,
          isSignedForm: bank.isSignedForm ?? null,
          // GST isGST flags — keyed by index
          ...(Array.isArray(record.gstList)
            ? Object.fromEntries(
                record.gstList.map((g, i) => [`isGST_${i}`, g.isGST ?? null]),
              )
            : {}),
        });

        const boolToYesNo = (v) =>
          v === true ? "yes" : v === false ? "no" : "";

        setFormData((prev) => ({
          ...prev,
          // Entity info
          parentName: record.entityName || entityNameFromUrl || prev.parentName,
          tradeName: record.tradeName || "",
          pinCode: record.pinCode || "",
          city: record.city || "",
          State: record.state || "",
          address: record.address || "",
          natureOfBusiness: record.natureOfBusiness || "",
          isInvoiceRequired: boolToYesNo(record.invoiceRequired),
          entityType: record.entityType || "",
          CompanypanNumber: prop.panCardNumber || pan.panCardNumber || "",
          // GST
          isGSTRegistered: boolToYesNo(record.isGSTRegistered),
          // Utility
          utilityType: utility.utilityType || "",
          utilityProviderName: utility.providerName || "",
          utilityAccountNumber: utility.accountOrConsumerNumber || "",
          utilityAddress: utility.addressOnUtilityBill || "",
          // MSME
          isMSMERegistered: boolToYesNo(msme.isRegisteredUnderMSME),
          msmeNumber: msme.msmeRegistrationNumber || "",
          // Contact
          contactPersonName: contact.contactPersonName || "",
          contactPersonDesignation: contact.designation || "",
          contactPersonMobile: contact.mobileNumber || "",
          contactEmail: contact.contactPersonEmail || record.emailAddress || "",
          // Bank
          hasBankDetails: boolToYesNo(bank.provideBankDetails),
          bankName: bank.bankName || "",
          accountNumber: bank.accountNumber || "",
          ifscCode: bank.ifscCode || "",
          accountType: bank.accountType || "",
          branchName: bank.branchName || "",
          beneficiaryName: bank.beneficiaryName || "",
          relationWithBeneficiary: bank.relationWithBeneficiary || "",
          thirdPartyRelation: bank.relationWithBeneficiary || "",
        }));

        // Document number inputs
        setEntityDocInputs({
          doc_aadhar_number:
            prop.aadharCardNumber || aadhar.aadharCardNumber || "",
          doc_pan_number: prop.panCardNumber || pan.panCardNumber || "",
          doc_llpCertificate_number: llp.llpCrtificateNumber || "",
          doc_partnershipDeed_ref: partner.partnershipDeedPath ? "" : "", // no number field for deed
          doc_aadharBoth_numbers:
            partner.aadharNumberOfBothPartners ||
            llp.aadharNumberOfBothPartners ||
            "",
          doc_coi_number: pvt.certificateOfIncorporationNumber || "",
          doc_directorPan_number: pvt.directorPANNumber || "",
          doc_directorAadhar_number: pvt.directorAadharNumber || "",
          doc_trustDeed_ref: trust.trustDeedNumber || "",
          doc_panTrust_number: trust.trustPanNumber || "",
          doc_aadharTrustee_number: trust.trusteeAadharNumber || "",
          doc_panTrustee_number: trust.trusteePanNumber || "",
        });

        // GST entries
        if (Array.isArray(record.gstList) && record.gstList.length > 0) {
          setGstEntries(
            record.gstList.map((g) => ({
              gstNumber: g.gstNumber || "",
              gstState: g.state || "",
              gstAddress: g.gstAddress || "",
            })),
          );
          setGstFiles(record.gstList.map(() => null));
          setGstErrors(record.gstList.map(() => ""));
          setExistingPaths((prev) => {
            const updated = { ...prev };
            record.gstList.forEach((g, i) => {
              if (g.gstCertificatePath)
                updated[`gst_file_${i}`] = g.gstCertificatePath;
            });
            return updated;
          });
        }
      },
    );
  }, [onboardingIdFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // GST entry handlers
  const addGstEntry = () => {
    setGstEntries((prev) => [
      ...prev,
      { gstNumber: "", gstState: "", gstAddress: "" },
    ]);
    setGstFiles((prev) => [...prev, null]);
    setGstErrors((prev) => [...prev, ""]);
  };
  const removeGstEntry = (index) => {
    setGstEntries((prev) => prev.filter((_, i) => i !== index));
    setGstFiles((prev) => prev.filter((_, i) => i !== index));
    setGstErrors((prev) => prev.filter((_, i) => i !== index));
  };
  const handleGstChange = (index, field, value) => {
    setGstEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry;
        const updated = {
          ...entry,
          [field]: field === "gstNumber" ? value.toUpperCase() : value,
        };
        if (field === "gstNumber") {
          updated.gstState = getStateFromGST(value.toUpperCase());
          setGstErrors((errs) => errs.map((e, ei) => (ei === index ? "" : e)));
        }
        return updated;
      }),
    );
  };
  const handleGstBlur = (index) => {
    const gstNumber = gstEntries[index]?.gstNumber || "";
    if (!gstNumber) return;

    // First validate GST format
    const gstFormatResult = validateGST(gstNumber);
    if (gstFormatResult !== true) {
      setGstErrors((prev) =>
        prev.map((e, ei) => (ei === index ? gstFormatResult : e)),
      );
      return;
    }

    // Then validate GST-PAN match
    const companyPAN =
      formData.CompanypanNumber || entityDocInputs["doc_pan_number"] || "";
    if (companyPAN) {
      const panMatchResult = validateGSTPANMatch(gstNumber, companyPAN);
      if (!panMatchResult.isValid) {
        setGstErrors((prev) =>
          prev.map((e, ei) => (ei === index ? panMatchResult.message : e)),
        );
        toast.error(`GST Entry ${index + 1}: ${panMatchResult.message}`, {
          position: "top-right",
          autoClose: 8000,
        });
        return;
      }
    }

    // Clear error if all validations pass
    setGstErrors((prev) => prev.map((e, ei) => (ei === index ? "" : e)));
  };
  const handleGstFileChange = (index, file) => {
    setGstFiles((prev) => prev.map((f, i) => (i === index ? file : f)));
  };

  // Seed entity name from URL param — used when no API prefill, or as initial value
  useEffect(() => {
    if (entityNameFromUrl) {
      setFormData((prev) => ({
        ...prev,
        // Only set if not already filled by API prefill
        parentName: prev.parentName || entityNameFromUrl,
      }));
    }
  }, [entityNameFromUrl]);

  // Pincode auto-fill — skip the initial trigger right after API prefill
  // (city/State already set from record). Re-enables when user manually edits pinCode.
  usePincodeAutofill({
    pincode: formData.pinCode,
    skipOnMount: true,
    onSuccess: ({ city, state }) => {
      setFormData((prev) => ({ ...prev, city, State: state }));
      setErrors((prev) => {
        const e = { ...prev };
        delete e.pinCode;
        return e;
      });
    },
    onError: (msg) => {
      setFormData((prev) => ({ ...prev, city: "", State: "" }));
      setErrors((prev) => ({ ...prev, pinCode: msg }));
    },
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    const uppercaseFields = [
      "CompanypanNumber",
      "ifscCode",
      "thirdPartyIfscCode",
    ];
    if (uppercaseFields.includes(name) && typeof newValue === "string")
      newValue = newValue.toUpperCase();

    const digitsOnlyFields = [
      "contactPersonMobile",
      "accountNumber",
      "thirdPartyAccountNumber",
    ];
    if (digitsOnlyFields.includes(name) && typeof newValue === "string")
      newValue = newValue.replace(/\D/g, "");

    if (name === "pinCode") {
      newValue = newValue.replace(/\D/g, "").slice(0, 6);
    }

    if (name === "hasBankDetails" && newValue === "no") {
      setErrors((prev) => {
        const e = { ...prev };
        [
          "bankName",
          "accountNumber",
          "ifscCode",
          "accountType",
          "branchName",
          "beneficiaryName",
        ].forEach((f) => delete e[f]);
        return e;
      });
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    if (name === "tradeName" || name === "beneficiaryName") {
      setBeneficiaryMatchStatus(null);
    }

    if (type !== "checkbox" && errors[name]) {
      setErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (!value || !value.trim()) return;

    if (name === "beneficiaryName" || name === "tradeName") {
      const trade = (name === "tradeName" ? value : formData.tradeName)
        .trim()
        .toLowerCase();
      const beneficiary = (
        name === "beneficiaryName" ? value : formData.beneficiaryName
      )
        .trim()
        .toLowerCase();
      if (trade && beneficiary) {
        setBeneficiaryMatchStatus(trade === beneficiary ? "match" : "mismatch");
      }
      return;
    }

    const validators = {
      // Form fields
      CompanypanNumber: validatePAN,
      contactPersonMobile: validateMobile,
      ifscCode: validateIFSC,
      accountNumber: validateAccountNumber,
      thirdPartyIfscCode: validateIFSC,
      thirdPartyAccountNumber: validateAccountNumber,
      // Document number input fields (entityDocInputs)
      doc_aadhar_number: validateAadhar,
      doc_aadharBoth_numbers: validateAadhar,
      doc_aadharTrustee_number: validateAadhar,
      doc_directorAadhar_number: validateAadhar,
      doc_pan_number: validatePAN,
      doc_directorPan_number: validatePAN,
      doc_panTrust_number: validatePAN,
      doc_panTrustee_number: validatePAN,
    };
    const validator = validators[name];
    if (validator) {
      const result = validator(value);
      if (result !== true) setErrors((prev) => ({ ...prev, [name]: result }));
      else
        setErrors((prev) => {
          const e = { ...prev };
          delete e[name];
          return e;
        });
    }
  };

  const validate = () => {
    const newErrors = {};
    ALL_FIELDS.forEach((field) => {
      // Skip bank fields entirely when user chose not to provide bank details
      if (
        BANK_FIELDS.some((b) => b.name === field.name) &&
        formData.hasBankDetails !== "yes"
      )
        return;

      if (
        field.conditionalOn &&
        formData[field.conditionalOn] !== field.conditionalValue
      )
        return;
      const value = formData[field.name];
      if (field.required && (!value || value.toString().trim() === "")) {
        newErrors[field.name] = `${field.label} is required`;
      } else if (field.validation && value) {
        const result = field.validation(value);
        if (result !== true) newErrors[field.name] = result;
      }
    });

    if (formData.isGSTRegistered === "no") {
      UTILITY_FIELDS.forEach((field) => {
        if (field.required && !formData[field.name]?.trim())
          newErrors[field.name] = `${field.label} is required`;
      });
    }

    if (showThirdParty) {
      THIRD_PARTY_FIELDS.forEach((field) => {
        const value = formData[field.name];
        if (field.required && (!value || value.toString().trim() === "")) {
          newErrors[field.name] = `${field.label} is required`;
        } else if (field.validation && value) {
          const result = field.validation(value);
          if (result !== true) newErrors[field.name] = result;
        }
      });
    }

    // Bank document upload — required when bank details are provided
    if (formData.hasBankDetails === "yes") {
      const hasBankDocUploaded =
        bankFiles["doc_bank_proof"] ||
        existingDocuments.some(
          (d) =>
            d.key === "doc_bank_proof" && d.files?.some((f) => f.isUploaded),
        );
      if (!hasBankDocUploaded) {
        newErrors["doc_bank_proof"] =
          "Bank Document (Passbook / Cancelled Cheque) is required";
      }
    }

    // GST certificate — required for each GST entry when GST is registered
    if (formData.isGSTRegistered === "yes") {
      const companyPAN =
        formData.CompanypanNumber || entityDocInputs["doc_pan_number"] || "";

      gstEntries.forEach((entry, i) => {
        // Check if GST certificate is uploaded
        const hasUploaded =
          uploadedDocs[`gst_file_${i}`]?.filePath ||
          existingPaths[`gst_file_${i}`] ||
          existingDocuments.some(
            (d) =>
              d.key === `gst_file_${i}` && d.files?.some((f) => f.isUploaded),
          );
        if (!hasUploaded) {
          newErrors[`gst_certificate_${i}`] =
            `GST Certificate for entry ${i + 1} (${entry.gstNumber || "GST #" + (i + 1)}) is required`;
        }

        // Validate GST-PAN match
        if (entry.gstNumber && companyPAN) {
          const panMatchResult = validateGSTPANMatch(
            entry.gstNumber,
            companyPAN,
          );
          if (!panMatchResult.isValid) {
            newErrors[`gst_pan_match_${i}`] = panMatchResult.message;
            // Also set the GST error for that specific entry
            setGstErrors((prev) =>
              prev.map((e, ei) => (ei === i ? panMatchResult.message : e)),
            );
          }
        }
      });
    }

    return newErrors;
  };

  const handleSubmit = async (e, isSaveAsDraft = false) => {
    if (e && e.preventDefault) e.preventDefault();

    // Skip validation for Save Draft
    if (!isSaveAsDraft) {
      const validationErrors = validate();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        toast.error("Please fix the errors before submitting");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const loggedInUser = getUserData();
      const now = new Date().toISOString();
      const entityPrimaryId = parseInt(
        searchParams.get("onboardingId") ||
          searchParams.get("entityPrimaryId") ||
          "0",
        10,
      );

      // Helper: get filePath — new upload takes priority, falls back to existing path from API
      const getPath = (documentType) =>
        uploadedDocs[documentType]?.filePath ||
        existingPaths[documentType] ||
        "";

      const hasPath = (documentType) => !!getPath(documentType);

      // Map from doc key (e.g. "doc_aadhar") to the storage key used in uploadedDocs
      // (e.g. "AadharCardPath"). If a new file was uploaded for a deficient doc,
      // its *Def flag should be cleared (set to false) on submit.
      const DOC_KEY_TO_STORAGE_KEY = {
        doc_aadhar: "AadharCardPath",
        doc_pan: "PANCardPath",
        doc_partnershipDeed: "PartnershipDeedPath",
        doc_aadharBoth: "AadharOfBothPartnersPath",
        doc_llpCertificate: "LLPCertificatePath",
        doc_coi: "CertificateOfIncorporationPath",
        doc_directorPan: "DirectorPANPath",
        doc_directorAadhar: "DirectorAadharPath",
        doc_trustDeed: "TrustDeedPath",
        doc_panTrust: "PANOfTrustPath",
        doc_panTrustee: "PANOfTrusteePath",
        doc_aadharTrustee: "AadharOfTrusteePath",
        doc_msme_certificate: "MSMECertificatePath",
        doc_utility_bill: "UtilityBillDocumentPath",
        doc_bank_proof: "PassbookOrCancelledChequePath",
        third_party_file: "UploadSignedFormPath",
      };

      /**
       * Returns the *Def flag value for a doc key.
       * If the doc was deficient AND the user has re-uploaded it in this session,
       * the flag is cleared to false (deficiency resolved).
       */
      const getDefFlag = (docKey) => {
        if (!deficiencyMap[docKey]) return false; // was never deficient
        const storageKey = DOC_KEY_TO_STORAGE_KEY[docKey];
        const wasReUploaded = storageKey ? !!uploadedDocs[storageKey] : false;
        return !wasReUploaded; // false if re-uploaded, true if still pending
      };

      /**
       * Returns the isXxx flag value for a doc.
       * - API returned a value (true/false) → pass that exact value
       * - No API value (new entity, first submit) → false
       */
      const getIsFlag = (flagName) => {
        const apiVal = existingIsFlags[flagName];
        return apiVal !== null && apiVal !== undefined ? apiVal : false;
      };

      // Build uploadedList — new uploads first, then existing paths not re-uploaded
      const newUploads = Object.values(uploadedDocs).map((doc) => ({
        success: true,
        message: "",
        id: doc.id ?? 0,
        fileName: doc.fileName || "",
        filePath: doc.filePath || "",
        documentType: doc.documentType || "",
        extension: doc.extension || "",
      }));

      // Add existing paths that were NOT re-uploaded in this session
      const newDocTypes = new Set(
        Object.values(uploadedDocs).map((d) => d.documentType),
      );
      const existingUploads = Object.entries(existingPaths)
        .filter(([key, path]) => path && !newDocTypes.has(key))
        .map(([key, path]) => ({
          success: true,
          message: "",
          id: 0,
          fileName: path.split(/[\\/]/).pop() || key,
          filePath: path,
          documentType: key,
          extension: path.includes(".") ? "." + path.split(".").pop() : "",
        }));

      const uploadedList = [...newUploads, ...existingUploads];

      // Build gstList — each entry gets its own cert path from indexed slot
      const gstList = gstEntries.map((entry, i) => ({
        id: i + 1,
        gstNumber: entry.gstNumber || "",
        state: entry.gstState || "",
        gstAddress: entry.gstAddress || "",
        gstCertificatePath:
          uploadedDocs[`gst_file_${i}`]?.filePath ||
          existingPaths[`gst_file_${i}`] ||
          "",
      }));

      const payload = {
        // ── Identifiers ───────────────────────────────────────────────────────
        entityPrimaryId,
        kycId: "",
        entityName: formData.parentName || "",
        tradeName: formData.tradeName || "",
        pinCode: formData.pinCode || "",
        city: formData.city || "",
        state: formData.State || "",
        address: formData.address || "",
        natureOfBusiness: formData.natureOfBusiness || "",
        invoiceRequired:
          formData.isInvoiceRequired === "yes"
            ? true
            : formData.isInvoiceRequired === "no"
              ? false
              : null,
        entityType: formData.entityType || "",

        // ── Contact person ────────────────────────────────────────────────────
        contactPersonDetails: {
          contactPersonName: formData.contactPersonName || "",
          designation: formData.contactPersonDesignation || "",
          mobileNumber: formData.contactPersonMobile || "",
          contactPersonEmail: formData.contactEmail || "",
        },

        // ── PAN ───────────────────────────────────────────────────────────────
        panDetails: {
          panCardNumber: entityDocInputs["doc_pan_number"] || "",
          panCardPath: getPath("PANCardPath") || "",
          panCardUploaded: hasPath("PANCardPath"),
          isPANCard: getIsFlag("isPANCard"),
          isPANCardDef: getDefFlag("doc_pan"),
        },

        // ── Aadhar ────────────────────────────────────────────────────────────
        adharDetails: {
          aadharCardNumber: entityDocInputs["doc_aadhar_number"] || "",
          aadharCardPath: getPath("AadharCardPath") || "",
          aadharCardUploaded: hasPath("AadharCardPath"),
          isAadharCard: getIsFlag("isAadharCard"),
          isAadharCardDef: getDefFlag("doc_aadhar"),
        },

        // ── Proprietorship ────────────────────────────────────────────────────
        proprietorshipDetails: {
          aadharCardNumber: entityDocInputs["doc_aadhar_number"] || "",
          aadharCardPath: getPath("AadharCardPath") || "",
          aadharCardUploaded: hasPath("AadharCardPath"),
          isAadharCard: getIsFlag("isAadharCard"),
          isAadharCardDef: getDefFlag("doc_aadhar"),
          panCardNumber: entityDocInputs["doc_pan_number"] || "",
          panCardPath: getPath("PANCardPath") || "",
          panCardUploaded: hasPath("PANCardPath"),
          isPANCard: getIsFlag("isPANCard"),
          isPANCardDef: getDefFlag("doc_pan"),
        },

        // ── Partnership ───────────────────────────────────────────────────────
        partnershipDetails: {
          partnershipDeedPath: getPath("PartnershipDeedPath") || "",
          partnershipDeedUploaded: hasPath("PartnershipDeedPath"),
          isPartnershipDeed: getIsFlag("isPartnershipDeed"),
          isPartnershipDeedDef: getDefFlag("doc_partnershipDeed"),
          aadharNumberOfBothPartners:
            entityDocInputs["doc_aadharBoth_numbers"] || "",
          aadharOfBothPartnersPath: getPath("AadharOfBothPartnersPath") || "",
          aadharOfBothPartnersUploaded: hasPath("AadharOfBothPartnersPath"),
          isAadharOfBothPartners: getIsFlag("isAadharOfBothPartners"),
          isAadharOfBothPartnersDef: getDefFlag("doc_aadharBoth"),
        },

        // ── LLP ───────────────────────────────────────────────────────────────
        llpDetails: {
          llpCrtificateNumber:
            entityDocInputs["doc_llpCertificate_number"] || "",
          uploadLLPCertificatePath: getPath("LLPCertificatePath") || "",
          llpCertificateUploaded: hasPath("LLPCertificatePath"),
          isLLPCertificate: getIsFlag("isLLPCertificate"),
          isLLPCertificateDef: getDefFlag("doc_llpCertificate"),
          partnershipDeedPath: getPath("PartnershipDeedPath") || "",
          partnershipDeedUploaded: hasPath("PartnershipDeedPath"),
          isPartnershipDeed: getIsFlag("isPartnershipDeed"),
          isPartnershipDeedDef: getDefFlag("doc_partnershipDeed"),
          aadharNumberOfBothPartners:
            entityDocInputs["doc_aadharBoth_numbers"] || "",
          aadharOfBothPartnersPath: getPath("AadharOfBothPartnersPath") || "",
          aadharOfBothPartnersUploaded: hasPath("AadharOfBothPartnersPath"),
          isAadharOfBothPartners: getIsFlag("isAadharOfBothPartners"),
          isAadharOfBothPartnersDef: getDefFlag("doc_aadharBoth"),
        },

        // ── Private / Public Limited ──────────────────────────────────────────
        privatePublicLimitedCompanyDetails: {
          certificateOfIncorporationNumber:
            entityDocInputs["doc_coi_number"] || "",
          certificateOfIncorporationPath:
            getPath("CertificateOfIncorporationPath") || "",
          certificateOfIncorporationUploaded: hasPath(
            "CertificateOfIncorporationPath",
          ),
          isCertificateOfIncorporation: getIsFlag(
            "isCertificateOfIncorporation",
          ),
          isCertificateOfIncorporationDef: getDefFlag("doc_coi"),
          directorPANNumber: entityDocInputs["doc_directorPan_number"] || "",
          directorPANPath: getPath("DirectorPANPath") || "",
          directorPanUploaded: hasPath("DirectorPANPath"),
          isDirectorPan: getIsFlag("isDirectorPan"),
          isDirectorPanDef: getDefFlag("doc_directorPan"),
          directorAadharNumber:
            entityDocInputs["doc_directorAadhar_number"] || "",
          directorAadharPath: getPath("DirectorAadharPath") || "",
          directorAadharUploaded: hasPath("DirectorAadharPath"),
          isDirectorAadhar: getIsFlag("isDirectorAadhar"),
          isDirectorAadharDef: getDefFlag("doc_directorAadhar"),
        },

        // ── Trust ─────────────────────────────────────────────────────────────
        trustDetails: {
          trustDeedNumber: entityDocInputs["doc_trustDeed_ref"] || "",
          trustDeedPath: getPath("TrustDeedPath") || "",
          trustDeedUploaded: hasPath("TrustDeedPath"),
          isTrustDeed: getIsFlag("isTrustDeed"),
          isTrustDeedDef: getDefFlag("doc_trustDeed"),
          trustPanNumber: entityDocInputs["doc_panTrust_number"] || "",
          trustPanPath: getPath("PANOfTrustPath") || "",
          trustPanUploaded: hasPath("PANOfTrustPath"),
          isPANOfTrust: getIsFlag("isPANOfTrust"),
          isPANOfTrustDef: getDefFlag("doc_panTrust"),
          trusteePanNumber: entityDocInputs["doc_panTrustee_number"] || "",
          trusteePanPath: getPath("PANOfTrusteePath") || "",
          trusteePanUploaded: hasPath("PANOfTrusteePath"),
          isPANOfTrustee: getIsFlag("isPANOfTrustee"),
          isPANOfTrusteeDef: getDefFlag("doc_panTrustee"),
          trusteeAadharNumber:
            entityDocInputs["doc_aadharTrustee_number"] || "",
          trusteeAadharPath: getPath("AadharOfTrusteePath") || "",
          trusteeAadharUploaded: hasPath("AadharOfTrusteePath"),
          isAadharOfTrustee: getIsFlag("isAadharOfTrustee"),
          isAadharOfTrusteeDef: getDefFlag("doc_aadharTrustee"),
        },

        // ── MSME ──────────────────────────────────────────────────────────────
        msmeDetails: {
          isRegisteredUnderMSME: convertToBoolean(formData.isMSMERegistered),
          msmeRegistrationNumber:
            formData.isMSMERegistered === "yes"
              ? formData.msmeNumber || ""
              : "",
          msmeCertificatePath: getPath("MSMECertificatePath") || "",
          msmeCertificateUploaded: hasPath("MSMECertificatePath"),
          isMSMECertificate: getIsFlag("isMSMECertificate"),
          isMSMECertificateDef: getDefFlag("doc_msme_certificate"),
        },

        // ── Utility bill ──────────────────────────────────────────────────────
        utilityBillDetails: {
          utilityType: formData.utilityType || "",
          providerName: formData.utilityProviderName || "",
          accountOrConsumerNumber: formData.utilityAccountNumber || "",
          addressOnUtilityBill: formData.utilityAddress || "",
          utilityBillDocumentPath: getPath("UtilityBillDocumentPath") || "",
          utilityBillDocumentUploaded: hasPath("UtilityBillDocumentPath"),
          IsUtilityBillDocument: getIsFlag("isUtilityBillDocument"),
          isUtilityBillDocumentDef: getDefFlag("doc_utility_bill"),
        },

        // ── Bank details ──────────────────────────────────────────────────────
        bankDetails: {
          provideBankDetails: formData.hasBankDetails === "yes",
          bankName: formData.bankName || "",
          accountNumber: formData.accountNumber || "",
          ifscCode: formData.ifscCode || "",
          accountType: formData.accountType || "",
          branchName: formData.branchName || "",
          beneficiaryName: formData.beneficiaryName || "",
          relationWithBeneficiary: formData.thirdPartyRelation || "",
          passbookOrCancelledChequePath:
            getPath("PassbookOrCancelledChequePath") || "",
          passbookOrCancelledChequeUploaded: hasPath(
            "PassbookOrCancelledChequePath",
          ),
          isPassbookOrCancelledCheque: getIsFlag("isPassbookOrCancelledCheque"),
          isPassbookOrCancelledChequeDef: getDefFlag("doc_bank_proof"),
          uploadSignedFormPath: getPath("UploadSignedFormPath") || "",
          signedFormUploaded: hasPath("UploadSignedFormPath"),
          isSignedForm: getIsFlag("isSignedForm"),
          isSignedFormDef: getDefFlag("third_party_file"),
        },

        // ── GST ───────────────────────────────────────────────────────────────
        isGSTRegistered: convertToBoolean(formData.isGSTRegistered),
        gstList:
          formData.isGSTRegistered === "yes"
            ? gstList.map((g, i) => {
                // isGST flag: only true if API explicitly returned true, otherwise false
                const apiIsGstValue = existingIsFlags[`isGST_${i}`];
                const isGSTValue = apiIsGstValue === true ? true : false;

                return {
                  id: g.id || i + 1,
                  gstNumber: g.gstNumber || "",
                  state: g.state || "",
                  gstAddress: g.gstAddress || "",
                  gstCertificatePath: g.gstCertificatePath || "",
                  isGST: isGSTValue,
                  isGSTDef: getDefFlag(`gst_file_${i}`),
                };
              })
            : [],

        // ── Uploaded file list ────────────────────────────────────────────────
        uploadedList,

        // ── Metadata ─────────────────────────────────────────────────────────
        createdBy: loggedInUser?.userName || "",
        createdOn: now,
        modifiedBy: loggedInUser?.userName || "",
        modifiedOn: now,
        isDocumentUploaded: isSaveAsDraft ? false : true,
        isSaveAsDraft,
      };

      console.log("[Submit] Payload:", payload);
      const result = await sendEntityKYCFormDataAction(payload);
      if (result?.success) {
        if (isSaveAsDraft) {
          toast.success("Draft saved successfully!");
        } else {
          toast.success("KYC submitted successfully!");
          const entityName =
            formData.parentName || searchParams.get("EntityName") || "";
          const entityId =
            searchParams.get("entityId") || searchParams.get("EntityId") || "";
          navigate(
            `/thank-you?entityName=${encodeURIComponent(entityName)}&entityId=${encodeURIComponent(entityId)}`,
          );
        }
      } else {
        toast.error(
          result?.error ||
            (isSaveAsDraft ? "Failed to save draft" : "Failed to submit KYC"),
        );
      }
    } catch (err) {
      toast.error("An error occurred while submitting");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Document upload handler ────────────────────────────────────────────────
  const handleDocumentUpload = async (docKey, file) => {
    if (!file) return;

    // gst_file_0, gst_file_1 etc. → API type is always GSTCertificatePath
    // but we store the response keyed by the indexed slot so each entry is separate
    const isIndexedGst = /^gst_file_\d+$/.test(docKey);
    const documentType = isIndexedGst
      ? "GSTCertificatePath"
      : DOC_KEY_TO_API_TYPE[docKey] || docKey;
    // Storage key: use the full indexed key so each GST cert is stored separately
    const storageKey = isIndexedGst ? docKey : documentType;

    const entityId =
      searchParams.get("entityId") || searchParams.get("EntityId") || "";

    const result = await uploadOnboardingDocumentAction({
      entityId,
      id: 0,
      documentType,
      fileName: file.name.replace(/\.[^/.]+$/, ""),
      file,
    });

    if (result.success && result.data) {
      setUploadedDocs((prev) => ({
        ...prev,
        [storageKey]: { ...result.data, documentType },
      }));
    } else if (!result.success) {
      toast.error(`Failed to upload ${documentType}: ${result.error}`);
    }

    return result;
  };

  // ── Aadhar Visibility with OTP ────────────────────────────────────────────────
  const {
    otpModalOpen,
    currentAadharField,
    adminEmail,
    entityPrimaryId: aadharEntityId,
    handleEyeIconClick,
    handleOtpVerifySuccess,
    handleCloseOtpModal,
    getAadharDisplayValue,
    isAadharVisible,
    getCurrentFieldLabel,
    isAadharField,
  } = useAadharVisibilityData(
    entityDocInputs,
    parseInt(onboardingIdFromUrl, 10) || 0,
  );

  return {
    formData,
    errors,
    setErrors,
    isSubmitting,
    // departmentOptions, // TODO: re-enable when department dropdown is added
    handleChange,
    handleBlur,
    handleSubmit,
    handleSaveDraft: (e) => handleSubmit(e, true),
    handleDocumentUpload,
    existingDocuments,
    gstEntries,
    addGstEntry,
    removeGstEntry,
    handleGstChange,
    handleGstBlur,
    gstFiles,
    handleGstFileChange,
    gstErrors,
    utilityFile,
    setUtilityFile,
    msmeFile,
    setMsmeFile,
    entityId,
    thirdPartyFile,
    setThirdPartyFile,
    entityFiles,
    handleEntityFileChange,
    entityDocInputs,
    handleEntityDocInputChange,
    handleEntityDocInputBlur,
    bankFiles,
    handleBankFileChange,
    showThirdParty,
    beneficiaryMatchStatus,
    entityCategory,
    // entityLOBType,   // TODO: re-enable when shown in UI
    contactPersonEmail,
    spocEmailAddress,
    deficiencyMap,
    isDeficiencyMode,
    // Aadhar visibility
    otpModalOpen,
    currentAadharField,
    adminEmail,
    aadharEntityId,
    handleEyeIconClick,
    handleOtpVerifySuccess,
    handleCloseOtpModal,
    getAadharDisplayValue,
    isAadharVisible,
    getCurrentFieldLabel,
    isAadharField,
  };
};
